/**
 * SearchableSelect — V2 searchable dropdown (react-select wrapper).
 *
 * Drop-in replacement for a native <select> in V2 forms. Styled to match
 * .rp-field__input / .dp-drawer__input metrics via ./searchable-select.css.
 *
 * Why this exists alongside components/component-assets/SearchableDropdown.jsx:
 * that one is the V1 component (bootstrap markup + its own floating label, and
 * it composes option labels internally). This one takes pre-built options, so
 * callers keep their own domain logic, and it renders its menu in a body
 * portal so it is not clipped by scrolling drawers.
 *
 * Options may be flat or grouped — pass ONE of:
 *   options: [{ value, label, hint?, isDisabled?, searchText? }]
 *   groups:  [{ label, options: [...] }]
 * Switching between the two is a caller-side change only.
 *
 * Disabled options remain listed and searchable by design: an unavailable
 * choice (e.g. a room at max occupancy) should be findable so users can see
 * it exists rather than assume it is missing.
 *
 * @param {{
 *   id?: string,
 *   options?: Array<object>,
 *   groups?: Array<{ label: string, options: Array<object> }>,
 *   value?: string | number | null,
 *   onChange: (value: string | null) => void,
 *   placeholder?: string,
 *   isClearable?: boolean,
 *   isDisabled?: boolean,
 *   required?: boolean,
 *   ariaLabel?: string,
 *   noOptionsMessage?: string,
 *   menuPortal?: boolean,
 *   inputId?: string,
 * }} props
 */

import { useMemo, useCallback, useState, useEffect } from 'react';
import Select, { createFilter } from 'react-select';
import './searchable-select.css';

/**
 * Menu z-index. Mirrors --z-popover (600) so the menu clears --z-modal (500)
 * drawers/modals that host it.
 *
 * This MUST be applied through the `styles` prop rather than the stylesheet:
 * react-select injects its emotion styles at runtime, after our CSS, so at
 * equal specificity emotion's default (z-index: 1) wins and the menu renders
 * behind the drawer. Anything that has to beat an emotion default belongs in
 * `styles` below; purely additive rules can stay in searchable-select.css.
 */
const MENU_Z_INDEX = 600;

/** Token-driven overrides for react-select's emotion defaults. */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderRadius: 'var(--radius-lg)',
    borderColor: state.isFocused ? 'var(--color-border-focus)' : 'var(--color-border-medium)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--color-border-focus)' : 'var(--shadow-xs)',
    backgroundColor: state.isDisabled ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
    fontSize: 'var(--font-size-sm)',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--color-border-focus)' : 'var(--color-border-medium)',
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 var(--space-3)' }),
  singleValue: (base) => ({ ...base, color: 'var(--color-text-primary)' }),
  placeholder: (base) => ({ ...base, color: 'var(--color-text-tertiary)' }),
  input: (base) => ({ ...base, color: 'var(--color-text-primary)' }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--color-border)' }),
  dropdownIndicator: (base) => ({ ...base, color: 'var(--color-text-tertiary)' }),
  clearIndicator: (base) => ({ ...base, color: 'var(--color-text-tertiary)' }),
  menuPortal: (base) => ({ ...base, zIndex: MENU_Z_INDEX }),
  menu: (base) => ({
    ...base,
    zIndex: MENU_Z_INDEX,
    backgroundColor: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
    fontSize: 'var(--font-size-sm)',
  }),
  menuList: (base) => ({ ...base, backgroundColor: 'var(--color-bg-surface)', maxHeight: 260 }),
  groupHeading: (base) => ({
    ...base,
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-bg-surface)',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  }),
  option: (base, state) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-2)',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    // Disabled options stay visible and searchable on purpose — an
    // unavailable choice (a room at max occupancy) must remain findable so
    // users can see it exists rather than assume it is missing.
    color: state.isDisabled
      ? 'var(--color-text-muted)'
      : state.isSelected
        ? 'var(--color-text-inverse)'
        : 'var(--color-text-primary)',
    backgroundColor: state.isDisabled
      ? 'var(--color-bg-surface)'
      : state.isSelected
        ? 'var(--color-primary)'
        : state.isFocused
          ? 'var(--color-bg-tertiary)'
          : 'var(--color-bg-surface)',
    '&:active': {
      backgroundColor: state.isDisabled ? 'var(--color-bg-surface)' : 'var(--color-bg-tertiary)',
    },
  }),
  noOptionsMessage: (base) => ({ ...base, color: 'var(--color-text-muted)' }),
};

/** Option row: label on the left, muted hint (e.g. "Full") on the right. */
const Option = ({ label, hint }) => (
  <>
    <span className="ss-option__label">{label}</span>
    {hint ? <span className="ss-option__hint">{hint}</span> : null}
  </>
);

const SearchableSelect = ({
  id,
  inputId,
  options,
  groups,
  value,
  onChange,
  placeholder = 'Search or select...',
  isClearable = false,
  isDisabled = false,
  required = false,
  ariaLabel,
  noOptionsMessage = 'No matches',
  menuPortal = true,
}) => {
  // Menu open state is controlled so Escape can be handled deterministically.
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  // Drawers and modals close on Escape via listeners on `document`. An Escape
  // aimed at an open menu must close only the menu, not discard the form
  // behind it. Intercepting in the CAPTURE phase runs before those listeners,
  // so we stop the event there and close the menu ourselves.
  useEffect(() => {
    if (!menuIsOpen) return undefined;
    const onEscapeCapture = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setMenuIsOpen(false);
    };
    document.addEventListener('keydown', onEscapeCapture, true);
    return () => document.removeEventListener('keydown', onEscapeCapture, true);
  }, [menuIsOpen]);

  const selectOptions = useMemo(() => groups || options || [], [groups, options]);

  // Find the selected option across flat or grouped shapes. Compared as
  // strings so callers can hold either numeric ids or string state.
  const selectedOption = useMemo(() => {
    if (value === '' || value === null || value === undefined) return null;
    const target = String(value);
    const flat = groups
      ? groups.flatMap((g) => g.options || [])
      : (options || []);
    return flat.find((o) => String(o.value) === target) || null;
  }, [value, options, groups]);

  // Search against `searchText` when the caller provides it, so what is
  // matched is independent of how the label is composed.
  const filterOption = useMemo(
    () => createFilter({
      ignoreCase: true,
      ignoreAccents: true,
      trim: true,
      matchFrom: 'any',
      stringify: (candidate) => candidate.data?.searchText || candidate.label || '',
    }),
    []
  );

  const handleChange = useCallback(
    (option) => onChange(option ? option.value : null),
    [onChange]
  );

  return (
    <div id={id ? `${id}-wrap` : undefined}>
      <Select
        inputId={inputId || id}
        options={selectOptions}
        value={selectedOption}
        onChange={handleChange}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        isSearchable
        isClearable={isClearable}
        isDisabled={isDisabled}
        required={required}
        aria-label={ariaLabel}
        placeholder={placeholder}
        filterOption={filterOption}
        formatOptionLabel={(opt) => <Option label={opt.label} hint={opt.hint} />}
        noOptionsMessage={() => noOptionsMessage}
        // Portal the menu so it is not clipped by scrolling containers
        // (.rp-form / .dp-drawer bodies use overflow-y: auto).
        menuPortalTarget={menuPortal && typeof document !== 'undefined' ? document.body : undefined}
        menuPosition={menuPortal ? 'fixed' : 'absolute'}
        menuShouldScrollIntoView={false}
        styles={selectStyles}
        classNamePrefix="ss-select"
        className="ss-select"
      />
    </div>
  );
};

export default SearchableSelect;
